import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Device, EventTrigger, FleetDefaults } from "@app/shared/src/models";
import { Card, CardHead, Spinner, Table, Tag, Td, Th } from "@app/shared/src/components_v2";
import { triggersByCategory } from "../../helpers/alertCategories";
import {
    ALERT_FIELD_LABELS,
    alertSettingsDifferences,
    alertSettingsFromDefaults,
    readAlertSettings,
} from "../../helpers/alertSettings";
import {
    radarVideoDifferences,
    radarVideoFromDefaults,
    readRadarVideo,
} from "../../helpers/radarVideoSettings";
import {
    fleetCanEmail,
    notificationDifferences,
    notificationsFromDefaults,
    readNotificationSettings,
} from "../../helpers/notificationSettings";
import { ALERT_CATEGORY_LABELS } from "../../helpers/alertCategories";
import { siteLabel } from "../../helpers/deviceLabels";

/**
 * Where the fleet has drifted from its own defaults, and on what.
 *
 * The per-unit badges answer "is this truck standard?" one truck at a time, which is the wrong shape
 * for the question a director actually has: *which* trucks are not, and what is different about
 * them. Everything here is derived from the same comparisons the device pages use, so this table and
 * those badges cannot disagree.
 *
 * A section with nothing listed is a section every unit follows — which is the state a fleet is
 * supposed to be in, so it reads as reassurance rather than an empty table.
 */

const RADAR_FIELD_LABELS: Record<string, string> = {
    radarEnabled: "radar transmitter",
    chirpConfigId: "radar profile",
    radarTrackerConfigId: "radar tracker",
    videoTrackerConfigId: "camera tracker",
    dvrRecordingEnabled: "DVR recording",
    dvrResolutionX: "recording profile",
    dvrResolutionY: "recording profile",
    dvrFps: "recording profile",
    dvrBitrateBps: "recording profile",
    dvrRetentionHours: "retention",
    streamResolutionX: "streaming profile",
    streamResolutionY: "streaming profile",
    streamFps: "streaming profile",
    streamBitrateBps: "streaming profile",
};

type Row = {
    device: Device;
    alerts: string[];
    /** Undefined when the unit's full record could not be loaded — unknown, not "follows". */
    radar?: string[];
    notifications: string[];
};

export const FleetOverridesSection: React.FC<{
    devices: Device[];
    /**
     * Complete device records from useFullDevices. The roster in `devices` omits the sensor
     * config, so radar settings read off it are blanks — judged against those, every unit with its
     * transmitter on read as "overridden" and no unit ever followed a preset. The radar column is
     * only ever computed from these full copies.
     */
    fullDevices?: Device[];
    /** Triggers keyed by facility — one location per truck, so this resolves per device. */
    triggersByFacility?: Map<string, EventTrigger[]>;
    defaults?: FleetDefaults;
    isLoading?: boolean;
}> = ({ devices, fullDevices, triggersByFacility, defaults, isLoading }) => {
    const rows = useMemo<Row[]>(() => {
        if (!defaults) return [];
        const fleetAlerts = alertSettingsFromDefaults(defaults);
        const fleetRadar = radarVideoFromDefaults(defaults);
        const fleetNotifications = notificationsFromDefaults(defaults);
        const fullById = new Map((fullDevices ?? []).map((d) => [d.id, d]));

        return devices.map((device) => {
            const triggers = (device.facilityId && triggersByFacility?.get(device.facilityId)) || [];

            const alerts = alertSettingsDifferences(readAlertSettings(triggers, defaults), fleetAlerts).map(
                (k) => ALERT_FIELD_LABELS[k],
            );
            const full = fullById.get(device.id);
            const radar = full
                ? Array.from(
                      new Set(
                          radarVideoDifferences(readRadarVideo(full), fleetRadar).map((k) => RADAR_FIELD_LABELS[k] ?? k),
                      ),
                  )
                : undefined;
            const notifications = notificationDifferences(
                readNotificationSettings(triggersByCategory(triggers)),
                fleetNotifications,
                fleetCanEmail(defaults),
            ).map((c) => ALERT_CATEGORY_LABELS[c]);

            return { device, alerts, radar, notifications };
        });
    }, [devices, fullDevices, triggersByFacility, defaults]);

    const drifted = rows.filter((r) => r.alerts.length || (r.radar?.length ?? 0) || r.notifications.length);

    if (isLoading || !defaults || !fullDevices) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <CardHead
                title="Where the fleet differs"
                note={
                    devices.length === 0
                        ? undefined
                        : `${drifted.length} of ${devices.length} unit${devices.length === 1 ? "" : "s"} overridden`
                }
            />

            {devices.length === 0 ? (
                <p className="px-5 py-6 text-[13px] text-[var(--muted)]">No devices in this organization yet.</p>
            ) : drifted.length === 0 ? (
                <p className="px-5 py-6 text-[13px] text-[var(--muted)]">
                    Every unit follows the fleet defaults on all three sections.
                </p>
            ) : (
                <Table className="mt-2">
                        <thead>
                            <tr>
                                <Th>Truck</Th>
                                <Th>Alerts &amp; relays</Th>
                                <Th>Radar &amp; video</Th>
                                <Th>Notifications</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {drifted.map(({ device, alerts, radar, notifications }) => (
                                <tr key={device.id} className="hover:bg-[var(--surface-2)]">
                                    <Td label="Truck">
                                        {/* The TMA number is the identity a director recognises;
                                            the key settles which box it is across truck swaps. */}
                                        <Link
                                            to={`/devices/${device.id}/settings`}
                                            className="font-semibold text-[var(--accent)]"
                                        >
                                            {siteLabel(device)}
                                        </Link>
                                        <div className="font-mono text-[11px] text-[var(--muted)]">{device.deviceKey}</div>
                                    </Td>
                                    <DiffCell label="Alerts & relays" fields={alerts} deviceId={device.id} tab="alerts" />
                                    <DiffCell label="Radar & video" fields={radar} deviceId={device.id} tab="radar" />
                                    <DiffCell label="Notifications" fields={notifications} deviceId={device.id} tab="notify" />
                                </tr>
                            ))}
                        </tbody>
                </Table>
            )}

            <div className="border-t border-[var(--line)] bg-[var(--surface-2)] px-5 py-3 text-[12px] leading-relaxed text-[var(--muted)]">
                Overridden means the unit's values differ from the defaults above — it is worked out by comparing them, not
                recorded when someone edits, so this can never disagree with what a unit is actually running. Saving a default
                leaves these units alone.
            </div>
        </Card>
    );
};

/** Names what differs, and links straight to the tab that can change it. */
const DiffCell: React.FC<{ label: string; fields?: string[]; deviceId?: string; tab: string }> = ({ label, fields, deviceId, tab }) => (
    <Td label={label}>
        {fields === undefined ? (
            // The unit's full record could not be loaded, so neither "follows" nor "overridden"
            // would be honest here.
            <span className="text-[12px] text-[var(--faint)]" title="This unit's record could not be loaded">
                —
            </span>
        ) : fields.length === 0 ? (
            <Tag tone="mute">Follows default</Tag>
        ) : (
            <Link to={`/devices/${deviceId}/settings?tab=${tab}`} className="inline-flex flex-wrap items-center gap-1.5">
                <Tag tone="warn">Overridden</Tag>
                <span className="text-[12px] text-[var(--muted)]">{fields.join(", ")}</span>
            </Link>
        )}
    </Td>
);
